// Copyright (c) Docugami, Inc. All rights reserved.

import axios from 'axios';
import { DocxCustomProperty, getCustomProperty } from "components/doc_hosting/doc_hosts/word/util/WordMetadataHelpers";
import { UnassignedDocSetId } from 'components/file_management/file_helpers';
import { FileManagerInstance } from "filemanager/FileManager";
import jwt from 'jwt-decode';
import { BuildType } from "networking/BuildType";
import { Organization, OrganizationAccount, OrganizationState, WaitlistResponse } from "networking/OrganizationApi";
import { createSignalRConnection } from "networking/OrganizationSignalRConnection";
import { ReportOperationProcessedMessage } from "networking/reportsApi";
import { NotificationsManagerSingleton } from "notifications/NotificationsManager";
import { Dispatch } from 'redux';
import { DocSetReadyModel } from "store/clusters/samplesApi";
import { samplesApiSlice } from "store/clusters/samplesSlice";
import { frontendSlice } from 'store/frontend/frontendSlice';
import { handleLabelRepeatsChangedMessage, reportSessionId, reportsStreamChanged } from "store/reports/reportsSlice";
import { handleWorkPathExportInitialization, workPathsApi } from 'store/work_path/workPathSlice';
import { Telemetry } from 'telemetry/TelemetryManager';
import { AppDispatch, AppState } from '..';
import { Config } from "../../config";
import { BuildState, ClusterInfo, FileChangedMessage, LabelRepeatsChangedMessage, ModelStatsUpdatedMessage, QualityControlWarningsChangedMessage } from "../../networking/api";
import { getLocalStorageOrganizationId, setLocalStorageOrganizationId } from "../../utils/localStorageHelpers";
import { _updateClusterInfo, handleClusterCreated, handleDocumentUpdate, handleRefetchCluster, invalidateDocsetStatusFromDocumentUpdate, removeClusters, removeFromCluster, tryRefreshDocSetDocuments } from "../clusters/actions";
import { organizationApiSlice } from './organizationSlice';
import { ADD_PIPELINE_BUILD, CREATE_ORGANIZATION, CreateOrganizationState, OrganizationActionTypes, OrganizationListState, OrganizationReduxState, PipelineBuildState, PipelineBuildsState, REMOVE_PIPELINE_BUILD, SET_CREATE_ORGANIZATION_POLLING, SET_CURRENT_ORGANIZATION, SET_ORGANIZATION_ACCOUNT, SET_ORGANIZATION_LIST, SET_PIPELINE_BUILDS_STATE, SET_USER_LIST, UPDATE_ORGANIZATION } from "./types";

function setOrganizationListState(newState: OrganizationListState): OrganizationActionTypes {
    return {
        type: SET_ORGANIZATION_LIST,
        payload: newState
    };
}

function setCreateOrganizationState(newState: CreateOrganizationState): OrganizationActionTypes {
    return {
        type: CREATE_ORGANIZATION,
        payload: newState
    };
}

function setOrganizationAccount(newState: OrganizationAccount): OrganizationActionTypes {
    return {
        type: SET_ORGANIZATION_ACCOUNT,
        payload: newState
    }
}

function setUserList(newState: string[]): OrganizationActionTypes {
    return {
        type: SET_USER_LIST,
        payload: newState
    }
}

function setCurrentOrganization(newState: OrganizationReduxState): OrganizationActionTypes {
    // Set the organization header to use with api requests.
    (window as any)._organization_id = newState.id;

    setLocalStorageOrganizationId(newState.id);

    return {
        type: SET_CURRENT_ORGANIZATION,
        payload: newState
    };
}

function setCreateOrganizationPolling(newState: boolean): OrganizationActionTypes {
    return {
        type: SET_CREATE_ORGANIZATION_POLLING,
        payload: newState
    };
}

export function addPipelineBuild(build: PipelineBuildState): OrganizationActionTypes {
    return {
        type: ADD_PIPELINE_BUILD,
        payload: build
    }
}

function removePipelineBuild(build: PipelineBuildState): OrganizationActionTypes {
    return {
        type: REMOVE_PIPELINE_BUILD,
        payload: build
    }
}

function setPipelineBuildsState(state: PipelineBuildsState): OrganizationActionTypes {
    return {
        type: SET_PIPELINE_BUILDS_STATE,
        payload: state,
    };
}

export function connectToOrganizationSignalR(): any {
    return function (dispatch: AppDispatch, getState: (() => AppState)) {
        const state = getState();
        const orgId = state.organizations.currentOrganization?.id;

        if ((window as any)._OfficeMode || !orgId) {
            return;
        }

        const signalRConnection = createSignalRConnection(orgId);
        const connection = signalRConnection.connection;

        // Connect action methods.
        connection.on("addPipelineBuild", (build: PipelineBuildState) => {
            dispatch(addPipelineBuild(build));
        });

        connection.on("removePipelineBuild", (build: PipelineBuildState) => {
            dispatch(removePipelineBuild(build));

            if (!!build.buildRequest.clusterId &&
                build.state === BuildState.Succeeded &&
                build.buildRequest.buildType === BuildType.StreamingDgml) {
                dispatch(tryRefreshDocSetDocuments(build.buildRequest.clusterId))
            }
        });

        connection.on("updateOrganization", (organization: Organization) => {
            dispatch(updateOrganization(getOrganizationReduxState(organization)));
        });

        connection.on("updateOrganizationAccount", (organizationAccount: OrganizationAccount) => {
            if (organizationAccount.organizationId === orgId) {
                dispatch(setOrganizationAccount(organizationAccount));
            }
        });

        connection.on("docSetDeleted", (id: string) => {
            dispatch(removeClusters([id]));
            dispatch(workPathsApi.util.invalidateTags(['workPaths']));
        });

        connection.on("docSetCreated", (docSet: ClusterInfo) => {
            dispatch(handleClusterCreated(docSet));
            dispatch(organizationApiSlice.util.invalidateTags(['first-arrival']));
        });

        connection.on("docSetUpdated", (docSet: ClusterInfo) => {
            dispatch(_updateClusterInfo(docSet));
            dispatch(frontendSlice.util.invalidateTags([{ type: 'docset-status', id: docSet.id }]));
            dispatch(frontendSlice.util.invalidateTags([{ type: 'docset-documents', id: docSet.id }]));
        });

        connection.on("reportOperationProcessed", (payload: ReportOperationProcessedMessage) => {
            if (!!payload.operationId?.startsWith(reportSessionId)) {
                // This was an operation by this user so we've already handled it
                return;
            }

            if (!!payload.changes) {
                reportsStreamChanged.raise({
                    reportId: payload.reportId,
                    version: payload.version,
                    changes: payload.changes
                });
            }
        })

        connection.on("fileUpdated", (message: FileChangedMessage) => {
            dispatch(handleDocumentUpdate(message.document));
            dispatch(invalidateDocsetStatusFromDocumentUpdate(message.document.clusterId, message.document));
            dispatch(frontendSlice.util.invalidateTags([{ type: 'docset-documents', id: message.document.metadataId }]));
        });

        connection.on("fileDeleted", (message: FileChangedMessage) => {
            if (!!message.document.clusterId && message.document.clusterId !== UnassignedDocSetId) {
                dispatch(invalidateDocsetStatusFromDocumentUpdate(message.document.clusterId, message.document));
            }
            dispatch(removeFromCluster(message.document.clusterId, [message.document.folderId]));
            dispatch(frontendSlice.util.invalidateTags([{ type: 'docset-documents', id: message.document.metadataId }]));
        });

        connection.on("documentSetReady", (docSetReady: DocSetReadyModel) => {
            if (docSetReady.isSample) {
                dispatch(samplesApiSlice.util.invalidateTags(['orgSamples']));
                dispatch(frontendSlice.util.invalidateTags([{ type: 'docset-status', id: docSetReady.clusterId }]));
                dispatch(handleRefetchCluster(docSetReady.clusterId));
            }
        });

        connection.on("qualityControlWarningsChanged", (message: QualityControlWarningsChangedMessage) => {
            dispatch(frontendSlice.util.invalidateTags([{ type: 'quality-control-warnings', id: `${message.docsetId}.${message.fileId}` }]));
        });

        connection.on("modelStatsUpdated", (message: ModelStatsUpdatedMessage) => {
            dispatch(frontendSlice.util.invalidateTags([{ type: 'model-stats', id: message.docsetId }]));
        })

        connection.on("labelRepeatsChanged", (message: LabelRepeatsChangedMessage) => dispatch(handleLabelRepeatsChangedMessage(message)))

        FileManagerInstance.configureHub(connection);
        NotificationsManagerSingleton.configureHub(connection);

        connection.on("workPathExportInitializationEnded", handleWorkPathExportInitialization);

        signalRConnection.start();
    }
}

export function updateOrganization(updatedOrg: OrganizationReduxState): any {
    return function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        let state = getState();
        let curOrg = { ...state.organizations.currentOrganization }

        if (curOrg?.id === updatedOrg.id) {
            if (updatedOrg.initializing !== curOrg.initializing) {
                // our initialization state has changed, refresh page to setup properly
                window.location.href = `${state.authentication.loginUrl}/#oid=${curOrg.id}`;
            }
        }

        dispatch({ type: UPDATE_ORGANIZATION, payload: updatedOrg })
    }
}

export function getOrganizationReduxState(org: Organization): OrganizationReduxState {
    return {
        id: org.organizationId,
        name: org.name,
        state: org.state,
        initializing: org.state === OrganizationState.Migrating || org.state === OrganizationState.MigrationFailed,
        organizationAdmin: org.creatorEmail
    }
}

export function fetchBuilds(force?: boolean): any {
    return async function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        const state = getState();

        if (state.organizations.pipelineBuilds.fetchState.fetching ||
            !state.organizations.pipelineBuilds) {
            return;
        }

        if (!force && state.organizations.pipelineBuilds.fetchState.fetched) {
            return;
        }

        const originalState: PipelineBuildsState = { ...state.organizations.pipelineBuilds };
        try {
            let updatedState: PipelineBuildsState = {
                ...originalState,
                fetchState: {
                    ...originalState.fetchState,
                    fetching: true,
                    fetched: false
                }
            }

            dispatch(setPipelineBuildsState(updatedState));

            const response = await axios.get<PipelineBuildState[]>(`${Config.apiUrl}/builds`);

            updatedState = {
                ...originalState,
                activeBuilds: response.data,
                fetchState: {
                    ...originalState.fetchState,
                    fetching: false,
                    fetched: true
                }
            }

            dispatch(setPipelineBuildsState(updatedState));
        }
        catch (error) {
            dispatch(setPipelineBuildsState(originalState));
            Telemetry.trackException(error as Error);
        }
    }
}

export function pollCreateOrganizationStatus(statusUrl: string, getAccessTokenSilently: any): any {
    return function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        const statusIntervalInMilliseconds = 5000;
        if (getState().organizations.createOrganizationPolling) {
            return;
        }

        dispatch(setCreateOrganizationPolling(true));

        const checkStatus = async () => {
            try {
                const response = await axios.get(statusUrl);
                switch (response.status) {
                    case 201: // 'Created'
                        // the org is created.... refresh the entire organization
                        // list, which will also update the auth token and creation state
                        dispatch(fetchOrganizationList(getAccessTokenSilently));
                        return;
                    case 202: // 'Creating'
                        // Check status again later.
                        window.setTimeout(checkStatus, statusIntervalInMilliseconds);
                        return;
                    case 400:
                    case 404:
                    default:
                        // Log error and reset state back to before creation.
                        // TODO (Mike): What should we show the user?
                        // TODO (Mike): Need a scavenger to clean up any partially created
                        // cloud resources.
                        console.log(`Create organization failed: ${response.data.output}.`);
                        dispatch(setCreateOrganizationState({
                            unknown: false,
                            waiting: false,
                            creating: false,
                            created: false,
                            statusUrl: '',
                        }));
                        dispatch(setCreateOrganizationPolling(false));
                        return;
                }
            } catch (error) {
                console.log(`Check organization status failed: ${error}. Trying again in ${statusIntervalInMilliseconds} ms.`);
                window.setTimeout(checkStatus, statusIntervalInMilliseconds);
            }
        }

        window.setTimeout(checkStatus, statusIntervalInMilliseconds);
    }
}

export function fetchOrganizationAccount() {
    return async function (dispatch: Dispatch<any>): Promise<void> {
        try {
            const response = await axios.get<OrganizationAccount>(
                `${Config.organizationApiUrl}/organization/account`
            );
            if (!!response?.data) {
                dispatch(setOrganizationAccount(response.data));
            }
        } catch (error) {
            Telemetry.trackException(error as Error);
        }
    }
}

export function getUserList() {
    return function (dispatch: Dispatch<any>) {
        axios.get(
            `${Config.organizationApiUrl}/organization/users/filtered`
        ).then(response => {
            if (!response.data) {
                return;
            }
            let userList: string[] = [];
            for (let user of response.data) {
                userList.push(user.email);
            }
            dispatch(setUserList(userList));
        });
    }
}

export function addUserToOrg(email: string) {
    return async function (dispatch: Dispatch<any>) {
        await axios.post(
            `${Config.organizationApiUrl}/organization/users/add`,
            [email]
        ).then(() => {
            dispatch(getUserList());
        });
    }
}

export function removeUserFromOrg(email: string) {
    return async function (dispatch: Dispatch<any>) {
        await axios.delete(
            `${Config.organizationApiUrl}/organization/users/remove`,
            { data: [email] }
        ).then(() => {
            dispatch(getUserList());
        });
    }
}

export function createOrganization(
    organizationName: string,
    firstName: string,
    lastName: string,
    accessCode: string,
    getAccessTokenSilently: any) {
    return async function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        if (getState().organizations.creationState.creating) {
            return Promise.resolve();
        }

        dispatch(setCreateOrganizationState({
            unknown: true,
            waiting: false,
            creating: false,
            created: false,
            statusUrl: '',
        }));

        try {
            const response = await axios.post(
                `${Config.organizationApiUrl}/trial`,
                {
                    organizationName: organizationName,
                    firstName: firstName,
                    lastName: lastName,
                    bypassCode: accessCode
                });

            if (response.status === 201) {
                // we have a newly created organization.... refresh the entire organization
                // list, which will also update the auth token and creation state
                await fetchOrganizationListAsync(dispatch, getState, getAccessTokenSilently);
            }
        }
        catch (error) {
            Telemetry.trackException(error as Error);

            dispatch(setCreateOrganizationState({
                unknown: false,
                waiting: false,
                creating: false,
                created: false,
                statusUrl: '',
            }));
        }
    }
}

export function fetchOrganizationList(getAccessTokenSilently: any) {
    return async function (dispatch: Dispatch<any>, getState: (() => AppState)) {
        await fetchOrganizationListAsync(dispatch, getState, getAccessTokenSilently);
    }
}

async function fetchOrganizationListAsync(dispatch: Dispatch<any>, getState: (() => AppState), getAccessTokenSilently: any) {
    const currentState = getState();
    if (currentState.organizations.organizationList.fetchState.fetching) {
        return;
    }

    try {
        dispatch(setOrganizationListState({
            fetchState: {
                fetched: false,
                fetching: true,
            },
            indexed: {},
            allIds: [],
        }));

        const response = await axios.get(`${Config.organizationApiUrl}/Organization`);
        let organizations: Organization[] = response.data;

        if (organizations.length === 0) {
            let fallback = true;
            try {
                const waitlistResponse = await axios.get(`${Config.organizationApiUrl}/waitlist`);
                if (waitlistResponse.status === 200) {
                    const waitlist: WaitlistResponse = waitlistResponse.data;
                    console.log(`Waitlist status ${waitlist.organizationName} : ${waitlist.status}`);
                    dispatch(setCreateOrganizationState({
                        unknown: false,
                        waiting: waitlist.status === "waitlist",
                        creating: false,
                        created: false,
                        statusUrl: '',
                    }));
                    fallback = false;
                }
            } catch { }

            if (fallback) {
                dispatch(setCreateOrganizationState({
                    unknown: false,
                    waiting: false,
                    creating: false,
                    created: false,
                    statusUrl: '',
                }));
            }
        }

        // If the user only has a single organization that is actively being created then show the creation UI
        const creatingOrganization = organizations.length === 1 && organizations[0].state === OrganizationState.Creating ? organizations[0] : undefined;
        if (!!creatingOrganization) {
            dispatch(setCreateOrganizationState({
                unknown: false,
                waiting: false,
                creating: true,
                created: false,
                statusUrl: `${Config.organizationApiUrl}/Organization/create/status/${creatingOrganization.organizationId}`,
            }));

            dispatch(setCurrentOrganization(getOrganizationReduxState(creatingOrganization)));

            // Next dispatch the organization list response.
            dispatch(setOrganizationListState({
                fetchState: {
                    fetched: true,
                    fetching: false,
                },
                indexed: {},
                allIds: [],
            }));
        }
        else {
            organizations = organizations.filter(x => x.state !== OrganizationState.Creating);

            const ids: string[] = [];
            const dict: { [id: string]: OrganizationReduxState } = {};

            const accessToken = await getAccessTokenSilently();
            let decodedToken = jwt<any>(accessToken);

            for (const organization of organizations) {
                if (undefined === decodedToken[`https://docugami.com/organization/${organization.organizationId}/roles`]) {
                    const accessToken = await getAccessTokenSilently({ ignoreCache: true });
                    decodedToken = jwt<any>(accessToken);
                }

                ids.push(organization.organizationId);
                dict[organization.organizationId] = getOrganizationReduxState(organization);
            }

            if (ids.length > 0) {
                dispatch(setCreateOrganizationState({
                    unknown: false,
                    waiting: false,
                    creating: false,
                    created: true,
                    statusUrl: '',
                }));

                let docxOrgId = "";
                let curOrgSet = false;

                const setOrg = (orgId: string, msg: string) => {
                    console.log(msg);
                    dispatch(setCurrentOrganization(dict[orgId]));
                    curOrgSet = true;
                }

                if ((window as any)._OfficeMode) {
                    await Word.run(async (context) => {
                        docxOrgId = await getCustomProperty(context, DocxCustomProperty.OrgId);
                    });
                }

                if (!!docxOrgId) {
                    if (docxOrgId in dict) {
                        setOrg(docxOrgId, `Selected organization id ${docxOrgId} from docx custom properties.`);
                    }
                    else {
                        console.log(`User does not have access to the organization id ${docxOrgId} specified on the docx custom props.`)
                    }
                }

                // If the user specified an organization id on the url, use that organization. If not, we
                // will use the first organization returned.
                if (!curOrgSet
                    && currentState.urlHashParameters
                    && currentState.urlHashParameters.dictionary
                    && 'oid' in currentState.urlHashParameters.dictionary) {
                    // Confirm the user has permissions for the url specified organization id.
                    const oid = currentState.urlHashParameters.dictionary['oid'];

                    if (oid in dict) {
                        setOrg(oid, `Selected organization id ${oid} from url parameters.`);
                    }
                    else {
                        window.location.href = '/oops';
                    }
                }

                if (!curOrgSet) {
                    const lastOrgId = getLocalStorageOrganizationId();
                    if (!!lastOrgId && ids.findIndex(x => x === lastOrgId) !== -1) {
                        setOrg(lastOrgId, `User belongs to mutliple organizations, using last used org ${lastOrgId}.`)
                    }
                    else {
                        setOrg(ids[0], `Organization id not specified. Selecting the first returned by the server ${ids[0]}.`)
                    }
                }
            }

            dispatch(setOrganizationListState({
                fetchState: {
                    fetched: true,
                    fetching: false,
                },
                indexed: dict,
                allIds: ids,
            }));

        }
    }
    catch (error) {
        Telemetry.trackException(error as Error);
        dispatch(setOrganizationListState({
            fetchState: {
                fetched: false,
                fetching: false,
            },
            indexed: {},
            allIds: [],
        }));
    };
}
